import React, { useEffect, useState } from 'react';
import Colon from '../Colon/Colon';
import Box from '../../../../../Box';
import Text from '../../../../../Text';
import styles from './CountdownTimer.module.css';

type TimeLeft = {
  days?: number;
  hours: string;
  minutes: string;
  seconds: string;
};

type PromoBannerIntl = {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
};

type BoxedCountdownTimerProps = {
  initialSeconds?: number;
  format?: 'boxed';
  intl?: PromoBannerIntl;
};

type InlineCountdownTimerProps = {
  initialSeconds?: number;
  format: 'inline';
  intl: PromoBannerIntl;
  endsInLabel?: string;
};

export type CountdownTimerProps =
  | BoxedCountdownTimerProps
  | InlineCountdownTimerProps;

const getTimeLeft = (totalSeconds?: number, includeDays = false): TimeLeft => {
  if (!totalSeconds) {
    return includeDays
      ? { days: 0, hours: '0', minutes: '00', seconds: '00' }
      : { hours: '00', minutes: '00', seconds: '00' };
  }

  if (includeDays) {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return {
      days,
      hours: hours.toString(),
      minutes: minutes.toString(),
      seconds: seconds.toString(),
    };
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: seconds.toString().padStart(2, '0'),
  };
};

const InlineCountdown = ({
  timeLeft,
  intl,
  endsInLabel,
}: {
  timeLeft: TimeLeft;
  intl: PromoBannerIntl;
  endsInLabel?: string;
}) => {
  const value = (amount: number, unit: string, unitDisplay: 'long' | 'narrow') => (
    <Text className={styles.inlineValue} type="display6">
      {intl.formatNumber(amount, { style: 'unit', unit, unitDisplay })}
    </Text>
  );

  return (
    <span className={styles.inlineTimer}>
      <Text className={styles.inlineLabel} type="body2">
        {endsInLabel}
      </Text>
      {timeLeft.days != null && timeLeft.days > 0 && value(timeLeft.days, 'day', 'long')}
      {value(Number(timeLeft.hours), 'hour', 'narrow')}
      {value(Number(timeLeft.minutes), 'minute', 'narrow')}
      {value(Number(timeLeft.seconds), 'second', 'narrow')}
    </span>
  );
};

const CountdownTimer = (props: CountdownTimerProps) => {
  const { initialSeconds = 0 } = props;
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (seconds <= 0) return;
    const intervalId = setInterval(() => {
      setSeconds((prevSeconds) => Math.max(prevSeconds - 1, 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const timeLeft = getTimeLeft(seconds, props.format === 'inline');

  if (props.format === 'inline') {
    return <InlineCountdown
        timeLeft={timeLeft}
        intl={props.intl}
        endsInLabel={props.endsInLabel}
      />;
  }

  return (
    <Box className={styles.countdownTimer}>
      <Box className={styles.timeContainer}>
        <Text className={styles.timeLeft} type="display5">
          {timeLeft.hours}
        </Text>
        <Text className={styles.timeUnit} type="display6">
          H
        </Text>
      </Box>
      <Colon />
      <Box className={styles.timeContainer}>
        <Text className={styles.timeLeft} type="display5">
          {timeLeft.minutes}
        </Text>
        <Text className={styles.timeUnit} type="display6">
          M
        </Text>
      </Box>
      <Colon />
      <Box className={styles.timeContainer}>
        <Text className={styles.timeLeft} type="display5">
          {timeLeft.seconds}
        </Text>
        <Text className={styles.timeUnit} type="display6">
          S
        </Text>
      </Box>
    </Box>
  );
};

export default CountdownTimer;
